import React from "react";
import { appHistory } from "@/router/history";
import { withTranslation, type WithTranslation } from "react-i18next";
import { compose } from "helper/compose";

import { getDocumentLibrary } from "helper/documentModules";
import DocumentLibraryApi from "api/DocumentLibraryApi";
import { withModulesData, type ModulesQueryProps } from "api/queries/useModules";
import { getApiData } from "../../types/api";

interface SearchResult {
  id: number | string;
  title?: string;
  module?: string;
  publication_start?: string;
}

interface SearchLibrary {
  routePath?: string;
  title?: string;
  titleKey?: string;
  groupTitleKey?: string;
  parentSlug?: string;
}

interface TopbarSearchOwnProps extends WithTranslation {}

type TopbarSearchProps = TopbarSearchOwnProps & ModulesQueryProps;

const SEARCH_RESULTS_ID = "Topbar-search-results";

function TopbarSearch({ t, i18n, modules }: TopbarSearchProps) {
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  const searchTimerRef = React.useRef<number | null>(null);
  const latestSearchValueRef = React.useRef("");
  const searchRef = React.useRef<HTMLDivElement | null>(null);

  const clearSearchTimer = React.useCallback(() => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
  }, []);

  const runSearch = React.useCallback(
    (term: string) => {
      const normalizedSearch = term.trim();

      DocumentLibraryApi.search(normalizedSearch, i18n.resolvedLanguage || null, true)
        .then((response) => {
          if (latestSearchValueRef.current !== normalizedSearch) {
            return;
          }
          const content = getApiData(response, {}) as { items?: SearchResult[] };
          setResults(content.items || []);
          setIsSearching(false);
          setIsSearchOpen(true);
        })
        .catch(() => {
          if (latestSearchValueRef.current !== normalizedSearch) {
            return;
          }
          setResults([]);
          setIsSearching(false);
          setIsSearchOpen(true);
        });
    },
    [i18n]
  );

  React.useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!searchRef.current || searchRef.current.contains(event.target as Node)) {
        return;
      }
      setIsSearchOpen(false);
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      clearSearchTimer();
    };
  }, [clearSearchTimer]);

  React.useEffect(() => {
    if (search.trim().length >= 2) {
      runSearch(search);
    }
    // re-run only when language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  const handleSearchFocus = () => {
    if (results.length > 0 || search.trim().length >= 2) {
      setIsSearchOpen(true);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value || "";

    setSearch(next);
    setIsSearchOpen(next.trim().length >= 2);
    latestSearchValueRef.current = next.trim();

    clearSearchTimer();

    if (next.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimerRef.current = window.setTimeout(() => {
      runSearch(next);
    }, 220);
  };

  const getSearchResultLibrary = (result: SearchResult): SearchLibrary | null => {
    return getDocumentLibrary(modules, result && result.module) as SearchLibrary | null;
  };

  const getSearchResultUrl = (result: SearchResult): string => {
    const library = getSearchResultLibrary(result);
    if (!library || !library.routePath) {
      return "";
    }
    return library.routePath + "/document/" + result.id;
  };

  const handleSearchResultClick = (result: SearchResult) => {
    const url = getSearchResultUrl(result);
    if (!url) {
      return;
    }

    setSearch("");
    setResults([]);
    setIsSearching(false);
    setIsSearchOpen(false);
    appHistory.push(url);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsSearchOpen(false);
      return;
    }

    const first = results[0];
    if (event.key === "Enter" && first) {
      event.preventDefault();
      handleSearchResultClick(first);
    }
  };

  const formatResultDate = (value: string | undefined): string => {
    if (!value) {
      return "";
    }

    const date = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(i18n.resolvedLanguage || "fr", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const renderSearchResults = () => {
    const trimmed = search.trim();

    if (!isSearchOpen || trimmed.length < 2) {
      return null;
    }

    if (isSearching) {
      return (
        <div
          id={SEARCH_RESULTS_ID}
          className="Topbar-searchDropdown"
          role="listbox"
          aria-label={t("topbar.search_results_aria")}
        >
          <p className="Topbar-searchState">{t("topbar.search_loading")}</p>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div
          id={SEARCH_RESULTS_ID}
          className="Topbar-searchDropdown"
          role="listbox"
          aria-label={t("topbar.search_results_aria")}
        >
          <p className="Topbar-searchState">{t("topbar.search_empty")}</p>
        </div>
      );
    }

    return (
      <div
        id={SEARCH_RESULTS_ID}
        className="Topbar-searchDropdown"
        role="listbox"
        aria-label={t("topbar.search_results_aria")}
      >
        {results.map((result) => {
          const library = getSearchResultLibrary(result);
          if (!library) {
            return null;
          }

          const group = library.groupTitleKey
            ? t(library.groupTitleKey)
            : library.parentSlug
              ? t("navigation.modules." + library.parentSlug)
              : "";
          const translatedLibraryTitle = library.titleKey ? t(library.titleKey) : "";
          const libraryTitle =
            library.title ||
            (translatedLibraryTitle !== library.titleKey ? translatedLibraryTitle : "");
          const date = formatResultDate(result.publication_start);

          return (
            <button
              key={result.id}
              type="button"
              className="Topbar-searchResult"
              role="option"
              aria-selected="false"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSearchResultClick(result)}
            >
              <div className="Topbar-searchResultMeta">
                {group ? <span className="Topbar-searchResultCategory">{group}</span> : null}
                {group && libraryTitle ? (
                  <span className="Topbar-searchResultSeparator" aria-hidden="true"></span>
                ) : null}
                <span className="Topbar-searchResultSubcategory">{libraryTitle}</span>
                {date ? <span className="Topbar-searchResultDate">{date}</span> : null}
              </div>
              <strong className="Topbar-searchResultTitle">{result.title}</strong>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="Topbar-search"
      role="search"
      aria-label={t("topbar.search_aria")}
      ref={searchRef}
    >
      <span className="Topbar-searchIcon" aria-hidden="true"></span>
      <input
        className="Topbar-searchInput"
        type="search"
        value={search}
        onChange={handleSearchChange}
        onFocus={handleSearchFocus}
        onKeyDown={handleSearchKeyDown}
        placeholder={t("topbar.search_placeholder")}
        aria-label={t("topbar.search_placeholder")}
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-controls={SEARCH_RESULTS_ID}
        aria-expanded={isSearchOpen}
      />
      {renderSearchResults()}
    </div>
  );
}

export default compose(withTranslation(), withModulesData)(TopbarSearch);
